/*
 * Instant shade switching for lines sold as one product per shade (Creamy Lipstick #01, #02...).
 *
 * Clicking a shade swatch swaps the product section in place and updates the address bar to that shade's own page,
 * so it feels like choosing a variant while every shade keeps its own URL, title and Google ranking.
 * Falls back to a normal link when anything goes wrong.
 */

const cache = new Map();

const fetchShade = (url) => {
  if (!cache.has(url)) {
    cache.set(url, fetch(url, { credentials: 'same-origin' }).then((response) => {
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      return response.text();
    }).then((html) => new DOMParser().parseFromString(html, 'text/html')));
    cache.get(url).catch(() => cache.delete(url));
  }

  return cache.get(url);
};

const swapTo = async (url, { push = true, focusSelector } = {}) => {
  const currentSection = document.querySelector('[data-shade-mode="siblings"]')?.closest('.shopify-section');
  if (!currentSection) return false;

  const nextDocument = await fetchShade(url);
  const nextSection = nextDocument.getElementById(currentSection.id);
  if (!nextSection) return false;

  const apply = () => {
    currentSection.replaceWith(document.importNode(nextSection, true));
    document.title = nextDocument.title;

    for (const selector of ['meta[name="description"]', 'link[rel="canonical"]', 'meta[property="og:title"]', 'meta[property="og:url"]', 'meta[property="og:image"]']) {
      const next = nextDocument.head.querySelector(selector);
      const current = document.head.querySelector(selector);
      if (next && current) current.replaceWith(next.cloneNode(true));
    }

    if (focusSelector) {
      document.querySelector(focusSelector)?.focus({ preventScroll: true });
    }
  };

  if (document.startViewTransition && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    document.startViewTransition(apply);
  } else {
    apply();
  }

  if (push) {
    history.pushState({ shadeSwap: true }, '', url);
  }

  return true;
};

document.addEventListener('click', async (event) => {
  const link = event.target.closest('[data-shade-mode="siblings"] a.shade-chip');
  if (!link || event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
  if (link.getAttribute('aria-current') === 'true') {
    event.preventDefault();
    return;
  }

  event.preventDefault();
  event.stopImmediatePropagation();
  const url = link.href;

  try {
    const swapped = await swapTo(url, { focusSelector: `[data-shade-mode="siblings"] a.shade-chip[href="${link.getAttribute('href')}"]` });
    if (!swapped) window.location.href = url;
  } catch {
    window.location.href = url;
  }
}, true);

// Warm the next shade on hover or touch so the switch is instant
const warm = (event) => {
  const link = event.target.closest?.('[data-shade-mode="siblings"] a.shade-chip:not([aria-current="true"])');
  if (link) fetchShade(link.href).catch(() => {});
};

document.addEventListener('pointerenter', warm, true);
document.addEventListener('touchstart', warm, { passive: true, capture: true });

window.addEventListener('popstate', () => {
  if (!document.querySelector('[data-shade-mode="siblings"]')) return;

  swapTo(window.location.href, { push: false }).then((swapped) => {
    if (!swapped) window.location.reload();
  }).catch(() => window.location.reload());
});

if (!history.state && document.querySelector('[data-shade-mode="siblings"]')) {
  history.replaceState({ shadeSwap: true }, '', window.location.href);
}

// «Ενημέρωσέ με» on sold-out shades: show it only for an unavailable variant and name that variant in the message
document.addEventListener('variant:change', (event) => {
  const { formId, variant } = event.detail || {};
  const box = formId && document.querySelector(`[data-notify-for="${formId}"]`);
  if (!box) return;
  box.hidden = !variant || variant.available;
  const body = box.querySelector('[name="contact[body]"]');
  if (body && variant) body.value = `Ενημέρωση διαθεσιμότητας: ${box.dataset.productTitle} – ${variant.title} (${variant.id})`;
});

// Header search: show popular searches + best sellers until the visitor types (KIKO/MAC list item 3)
document.addEventListener('input', (event) => {
  const input = event.target.closest?.('.header-search__input');
  if (!input) return;
  const idle = input.closest('header-search')?.querySelector('[data-search-idle]');
  if (idle) idle.hidden = input.value.trim().length > 0;
});

// Gift with purchase (snippets/id-gift.liquid). Shopify's discount makes the gift free but never puts it in the cart,
// so «Πρόσθεσέ το στο καλάθι» adds it, and a line added as a gift (_gift) leaves the cart when the order drops below
// the minimum, so nobody pays for a gift by surprise.
const giftBoxes = () => document.querySelectorAll('[data-id-gift]');

const giftSections = () => {
  const sections = [];
  document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections } }));
  return sections.join(',');
};

const giftAnnounce = (cart, baseEvent) => {
  if (window.themeVariables?.settings?.pageType === 'cart') {
    window.location.reload();
    return;
  }
  document.documentElement.dispatchEvent(new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent, cart } }));
};

document.addEventListener('click', async (event) => {
  const button = event.target.closest?.('[data-id-gift-add]');
  const box = button?.closest('[data-id-gift]');
  if (!box) return;

  button.setAttribute('aria-busy', 'true');
  try {
    const response = await fetch(`${Shopify.routes.root}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ items: [{ id: Number(box.dataset.variant), quantity: 1, properties: { _gift: '1' } }], sections: giftSections() })
    });
    if (!response.ok) return;
    const added = await response.json();
    const cart = await (await fetch(`${Shopify.routes.root}cart.js`)).json();
    cart.sections = added.sections;
    giftAnnounce(cart, 'gift:add');
  } finally {
    button.removeAttribute('aria-busy');
  }
});

let giftSyncing = false;

const giftSync = async (cart) => {
  const box = giftBoxes()[0];
  if (!box || giftSyncing || !cart?.items) return;

  const minimum = Number(box.dataset.minimum);
  const giftProduct = Number(box.dataset.product);
  const counted = cart.items
    .filter((item) => item.product_id !== giftProduct && !item.gift_card && item.handle !== 'cod')
    .reduce((sum, item) => sum + item.final_line_price, 0);
  const giftLine = cart.items.find((item) => item.product_id === giftProduct && item.properties?._gift);
  if (!giftLine) return;

  const quantity = counted < minimum ? 0 : giftLine.quantity > 1 ? 1 : null;
  if (quantity === null) return;

  giftSyncing = true;
  try {
    const response = await fetch(`${Shopify.routes.root}cart/change.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: giftLine.key, quantity, sections: giftSections() })
    });
    if (!response.ok) return;
    if (quantity === 0) {
      try { sessionStorage.setItem('id-gift-removed', '1'); } catch {}
    }
    giftAnnounce(await response.json(), 'gift:sync');
  } finally {
    giftSyncing = false;
  }
};

// After a removal, say why once, in the refreshed drawer or on the reloaded cart page
const giftExplainRemoval = () => {
  let removed = false;
  try { removed = sessionStorage.getItem('id-gift-removed') === '1'; } catch {}
  const boxes = giftBoxes();
  if (!removed || boxes.length === 0) return;
  try { sessionStorage.removeItem('id-gift-removed'); } catch {}
  boxes.forEach((box) => {
    const note = document.createElement('p');
    note.textContent = box.dataset.removed;
    box.prepend(note);
  });
};

document.addEventListener('cart:change', (event) => giftSync(event.detail?.cart));
document.addEventListener('cart-drawer:refreshed', giftExplainRemoval);

giftExplainRemoval();

// The cart page reloads after every change instead of sending cart:change, so check the gift line on load there
if (window.themeVariables?.settings?.pageType === 'cart' && giftBoxes().length > 0) {
  fetch(`${Shopify.routes.root}cart.js`).then((response) => response.json()).then(giftSync).catch(() => {});
}
